import React, { useState } from 'react';

import { Autocomplete, Grid, InputLabel, Typography } from '@mui/material';
import { AlineAutocompleteInput, ConfirmationDialog } from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack } from 'src/helpers';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  deleteContainer: {
    marginTop: theme.spacing(2),
  },
  deletedEntityName: {
    overflowWrap: 'anywhere',
    textDecoration: 'line-through',
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(2),
  },
  listItem: {
    ...getTypographyForSx(theme.typography.subtitle1),
    marginBottom: theme.spacing(1),
  },
}));

type VariableConfigRow = Pick<Apollo.DocumentVariableConfig, 'id' | 'label' | '__typename'>;
type UserDefinedRoleRow = Pick<Apollo.UserDefinedRole, 'id' | 'roleName' | '__typename'>;
type ContractTypeRow = Pick<Apollo.ContractType, 'id' | 'value' | '__typename'>;
type PartyRow = Pick<Apollo.Party, 'id' | 'name' | '__typename'>;
type Entity = VariableConfigRow | UserDefinedRoleRow | ContractTypeRow | PartyRow;

interface MergeDialogProps<T extends Entity> {
  entities: T[];
  getDisabledReason: (targetId: UUID | null) => string;
  handleMerge: (targetId: UUID, sourceIds: UUID[]) => Promise<{ data?: unknown }>;
  message: string;
  onClose: () => void;
  open: boolean;
  selectedIds: UUID[];
  title: string;
  /**
   * Optional custom merge-target picker. Defaults to a static Autocomplete over
   * `availableTargets`. Provide this to supply a paginated/server-side picker
   * (e.g. parties), in which case the chosen target may not be in `entities`.
   */
  renderTargetPicker?: (args: {
    availableTargets: T[];
    onChange: (target: T | null) => void;
    value: T | null;
  }) => React.ReactNode;
}

export function MergeDialog<T extends Entity>({
  entities,
  getDisabledReason,
  handleMerge,
  message,
  onClose,
  open,
  renderTargetPicker,
  selectedIds,
  title,
}: MergeDialogProps<T>): JSX.Element {
  const { classes } = useStyles();
  const dispatchSuccessSnack = useDispatchSuccessSnack({
    key: uuid4(),
    message: 'Items successfully merged.',
  });
  /**
   * Holds the full target entity (not just its id) so a target picked from a
   * paginated source that isn't in `entities` still resolves for display/merge.
   */
  const [target, setTarget] = useState<T | null>(null);
  const targetId = target?.id ?? null;
  /**
   * If the selection changes (incl. a paginated picker), drop the chosen target
   * so it can never be stale relative to the available options/sources. This
   * adjusts state during render (React's recommended pattern) rather than in an
   * effect, so it resets before children see the stale value and stays safe
   * under `<Activity>` (which re-runs effect setups on show).
   */
  const selectionKey = [...selectedIds].sort().join(',');
  const [prevSelectionKey, setPrevSelectionKey] = useState(selectionKey);
  if (selectionKey !== prevSelectionKey) {
    setPrevSelectionKey(selectionKey);
    setTarget(null);
  }
  const selectedEntities = entities.filter(entity => selectedIds.includes(entity.id));
  const notSelectedEntities = entities.filter(entity => !selectedIds.includes(entity.id));
  // if the user is selecting multiple entities, the available targets are all the selected entities b/c they likely want to merge them all.
  const availableTargets = selectedEntities.length === 1 ? notSelectedEntities : selectedEntities;
  const sortedAvailableTargets = [...availableTargets].sort((a, b) =>
    getEntityName(a).localeCompare(getEntityName(b))
  );

  async function handleMergeEntities(): Promise<void> {
    if (!targetId) return;

    const { data } = await handleMerge(
      targetId,
      selectedIds.filter(id => id !== targetId)
    );
    if (data) dispatchSuccessSnack();
  }

  function getEntityName(entity: Entity): string {
    switch (entity.__typename) {
      case 'DocumentVariableConfig':
        return entity.label;
      case 'UserDefinedRole':
        return entity.roleName;
      case 'ContractType':
        return entity.value;
      case 'Party':
        return entity.name;
    }
  }

  function clearModalState(): void {
    // Avoids flicker in UI when state changes before modal closes.
    setTimeout(() => setTarget(null), 250);
  }

  return (
    <ConfirmationDialog
      message={message}
      open={open}
      primaryDisabledReason={getDisabledReason(targetId)}
      primaryText="Merge"
      title={title}
      onClickPrimary={handleMergeEntities}
      onClose={() => {
        clearModalState();
        onClose();
      }}
    >
      <Grid container className={classes.deleteContainer} spacing={2} wrap="nowrap">
        <Grid size={6}>
          <InputLabel className={classes.inputLabel}>These items will be deleted:</InputLabel>
          <ol>
            {selectedEntities
              .filter(entity => entity.id !== targetId)
              .sort((a, b) => {
                const aName = getEntityName(a);
                const bName = getEntityName(b);
                return aName.localeCompare(bName);
              })
              .map((entity, index) => {
                return (
                  <li key={index} className={classes.listItem}>
                    <Typography className={classes.deletedEntityName} variant="subtitle1">
                      {getEntityName(entity)}
                    </Typography>
                  </li>
                );
              })}
          </ol>
        </Grid>
        <Grid size={6}>
          <InputLabel className={classes.inputLabel}>Merge selected with:</InputLabel>
          {renderTargetPicker ? (
            renderTargetPicker({
              availableTargets: sortedAvailableTargets,
              onChange: setTarget,
              value: target,
            })
          ) : (
            <Autocomplete
              fullWidth
              getOptionKey={option => option.id}
              getOptionLabel={getEntityName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              options={sortedAvailableTargets}
              renderInput={params => (
                <AlineAutocompleteInput {...params} placeholder="Select a merge target..." />
              )}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Typography key={key} {...optionProps}>
                    {getEntityName(option)}
                  </Typography>
                );
              }}
              value={target}
              onChange={(_, newValue) => setTarget(newValue)}
            />
          )}
        </Grid>
      </Grid>
    </ConfirmationDialog>
  );
}
